import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { getUserProfile } from "../service/auth";
import WelcomeModal from "./WelcomeModal";
import LoginModal from "./LoginModal";
import ChooseEmojiModal from "./ChooseEmojiModal";

export const AuthContext = createContext();

const AUTH_STAGES = {
  UNAUTHENTICATED: "UNAUTHENTICATED",
  AUTHENTICATED_NO_EMOJI: "AUTHENTICATED_NO_EMOJI",
  AUTHENTICATED: "AUTHENTICATED",
  ESCAPE_AUTH: "ESCAPE_AUTH",
};

const INITIAL_AUTH_DETAILS = {
  id: null,
  phone_no: "",
  emoji: "",
  birthdate: "",
  zipcode: "",
  zebra_id: null,
  selectedPlan: null,
};

function AuthProvider({ children }) {
  const [authStage, setAuthStage] = useState(AUTH_STAGES.UNAUTHENTICATED);
  const [authDetails, setAuthDetails] = useState(INITIAL_AUTH_DETAILS);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const isAccessAllowed = useRef(false);
  const [isAuthFlowEnabled, setIsAuthFlowEnabled] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleNavigation = useCallback(
    (updatedDetails) => {
      setAuthDetails((prevAuthDetails) => ({
        ...prevAuthDetails,
        ...updatedDetails,
      }));

      // After emoji selection, move to authenticated state
      if (authStage === AUTH_STAGES.AUTHENTICATED_NO_EMOJI && updatedDetails.emoji) {
        setAuthStage(AUTH_STAGES.AUTHENTICATED);
      }
    },
    [authStage]
  );

  const handleEscapeAuth = useCallback(() => {
    handleLogout(AUTH_STAGES.ESCAPE_AUTH);
  }, []);

  const handleLogout = useCallback(
    (targetStage = AUTH_STAGES.UNAUTHENTICATED) => {
      localStorage.removeItem("authUser");
      localStorage.removeItem("authToken");
      localStorage.removeItem("hasCreatedAccount");
      localStorage.removeItem("lastZebraIdCheck");
      setAuthDetails(INITIAL_AUTH_DETAILS);
      setAuthStage(targetStage);
      setShowLoginModal(false);
      // Stay on current page, modal will appear if auth flow is enabled
    },
    []
  );

  const location = useLocation();

  useEffect(() => {
    setIsAuthFlowEnabled(
      !location.pathname.startsWith("/video/") &&
      !location.pathname.startsWith("/embed/") &&
      !location.pathname.startsWith("/terms") &&
      !location.pathname.startsWith("/privacy") &&
      !location.pathname.startsWith("/contactus")
    );
  }, [location.pathname]);

  useEffect(() => {
    isAccessAllowed.current = [
      AUTH_STAGES.AUTHENTICATED,
      AUTH_STAGES.AUTHENTICATED_NO_EMOJI,
      AUTH_STAGES.ESCAPE_AUTH,
    ].includes(authStage) || !isAuthFlowEnabled;
  }, [authStage, isAuthFlowEnabled]);

  const preventGuestAccess = useCallback(() => {
    if (!isAccessAllowed.current) {
      window.location.reload();
    }
  }, []);

  const toggleAuthFlow = useCallback((isEnabled) => {
    setIsAuthFlowEnabled(isEnabled);
  }, []);

  const updateZebraId = useCallback((zebra_id) => {
    setAuthDetails((prevDetails) => {
      const updatedDetails = { ...prevDetails, zebra_id };
      localStorage.setItem("authUser", JSON.stringify(updatedDetails));
      return updatedDetails;
    });
  }, []);

  const updateUserProfile = useCallback(({ id, zebra_id }) => {
    setAuthDetails((prevDetails) => {
      const updatedDetails = { ...prevDetails, id, zebra_id };
      localStorage.setItem("authUser", JSON.stringify(updatedDetails));
      return updatedDetails;
    });
  }, []);

  const handleSuccessfulSignup = useCallback((token, userData) => {
    // Store token and user data
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(userData));

    // Update auth state immediately
    setAuthDetails(userData);

    // Set appropriate auth stage based on emoji presence
    if (!userData.emoji) {
      setAuthStage(AUTH_STAGES.AUTHENTICATED_NO_EMOJI);
    } else {
      setAuthStage(AUTH_STAGES.AUTHENTICATED);
    }

    return true;
  }, []);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");

    if (authToken && authUser) {
      setAuthDetails(authUser);
      if (!authUser.emoji) {
        setAuthStage(AUTH_STAGES.AUTHENTICATED_NO_EMOJI);
      } else {
        setAuthStage(AUTH_STAGES.AUTHENTICATED);
      }
    } else {
      setAuthStage(AUTH_STAGES.UNAUTHENTICATED);
    }
    setIsAuthLoading(false);
  }, []);

  // Determine if we should show auth modal for unauthenticated users
  const shouldShowAuthModal = !isAuthLoading && authStage === AUTH_STAGES.UNAUTHENTICATED && isAuthFlowEnabled;

  // Fetch user profile to get zebra_id and id
  // Runs immediately if id is missing, otherwise once per day
  useEffect(() => {
    const fetchUserProfile = async () => {
      const authToken = localStorage.getItem("authToken");
      const lastProfileCheck = localStorage.getItem("lastZebraIdCheck");
      const now = new Date().getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000;

      // Fetch immediately if id is missing, or if 24 hours have passed
      const needsImmediateFetch = !authDetails.id;
      const needsDailyRefresh = !lastProfileCheck || now - Number(lastProfileCheck) > oneDayInMs;

      if (
        authToken &&
        (authStage === AUTH_STAGES.AUTHENTICATED || authStage === AUTH_STAGES.AUTHENTICATED_NO_EMOJI) &&
        (needsImmediateFetch || needsDailyRefresh)
      ) {
        try {
          const response = await getUserProfile();
          const zebra_id = response.data?.data?.zebra_id || null;
          const id = response.data?.data?.id || null;

          // Update authDetails with the latest zebra_id and id
          setAuthDetails((prevDetails) => {
            const updatedDetails = { ...prevDetails, zebra_id, id };
            // Update localStorage with the latest user data
            localStorage.setItem("authUser", JSON.stringify(updatedDetails));
            return updatedDetails;
          });

          // Store the timestamp of this check
          localStorage.setItem("lastZebraIdCheck", now.toString());
        } catch (error) {
          console.error("Failed to fetch user profile:", error);
        }
      }
    };

    fetchUserProfile();
  }, [authStage, authDetails.id]);

  return (
    <AuthContext.Provider
      value={{
        isAccessAllowed: isAccessAllowed.current,
        authDetails,
        navigateStage: handleNavigation,
        logout: handleLogout,
        escapeAuth: handleEscapeAuth,
        preventGuestAccess,
        toggleAuthFlow,
        updateZebraId,
        updateUserProfile,
        handleSuccessfulSignup,
        hasPartyPass: !!authDetails.zebra_id,
        userId: authDetails.id,
        needsEmojiPicker: authStage === AUTH_STAGES.AUTHENTICATED_NO_EMOJI,
        isAuthenticated: authStage === AUTH_STAGES.AUTHENTICATED || authStage === AUTH_STAGES.AUTHENTICATED_NO_EMOJI,
      }}
    >
      {children}
      {shouldShowAuthModal && !showLoginModal && (
        <WelcomeModal onLoginClick={() => setShowLoginModal(true)} />
      )}
      {shouldShowAuthModal && showLoginModal && (
        <LoginModal onBack={() => setShowLoginModal(false)} />
      )}
      {authStage === AUTH_STAGES.AUTHENTICATED_NO_EMOJI && (
        <ChooseEmojiModal />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthProvider;
